import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Container, CardHeader } from "reactstrap";
import useUrlState from "@ahooksjs/use-url-state";
import { useTranslation } from "react-i18next";

import {
  getDashboardLink,
  getSimulators6DOF,
  getSimulators6DOFHistory,
  getSimulatorsDetail6DOF,
  getContentFromLink,
  getPageIndexSimulator6DOF,
} from "src/services/simulator-gnc";
import { getFileUploadName, uniqArray, handleScrollTo, handleScrollToTop, calculateMCSSuccessRate } from "src/utils/common";
import {
  PROCESS_STATUS,
  DASHBOARD_STATUS,
  RETRY_TIME,
  DASHBOARD_INPUT_TYPE,
  DASHBOARD_TYPE,
  PAGINATION_DEFAULT,
  REFETCH_INTERVAL_TIME,
  INPUT_DATA_GNC_6DOF_MAPPING,
  TERMINAL_GNC_6DOF_MAPPING,
  SIMULATION_TYPE,
  RESOURCE_TYPE_FOR_TAG,
} from "src/constant/common";
import APP_ROUTES from "src/routes/routes";
import { useMutation, useQuery } from "src/Hooks/useReactQuery";
import { useRecoilState, useSetRecoilState } from "recoil";
import { alertState, resetFormPromptState } from "src/recoil/atoms/global";
import TableCommon from "src/components/Common/Table";
import ModalCaseResult from "../components/ModalCaseResult";
import ModalSeriesFilesView6DoF from "src/components/Common/Modal/ModelSerieFilesView6DoF";
import ModalReferenceHistory from "src/components/Common/Modal/ModalReferenceHistory";
import ModalAssignTags from "src/components/Common/Modal/ModalAssignTags";
import ProtectedComponent from "src/components/Permission";
import { CustomLink } from "src/components/Common/CustomLink";
import columns from "./columns";
import useGetAllTags from "src/Hooks/TagFunction/useGetTags";
import SearchInput from "src/components/Common/SearchInput";
import { getErrorDetails } from "src/Hooks/useErrorDetails";

const GNCSimulatorList6DOF = () => {
  const { t } = useTranslation(["gnc-simulator-6dof", "common"]);

  const showAlert = useSetRecoilState(alertState);
  const [resetForm, setResetForm] = useRecoilState(resetFormPromptState);

  const [params, setParams] = useUrlState(
    {},
    {
      navigateMode: "replace",
      stringifyOptions: {
        skipNull: true,
        skipEmptyString: true,
      },
    }
  );

  const [textSearch, setTextSearch] = useState(params?.condition || null);
  const [seriesById, setSeriesById] = useState();
  const [pageIndexId, setPageIndexById] = useState();
  const [isLoadingSimulator, setIsLoadingSimulator] = useState(true);
  const [simulationIdsOpen, setSimulationIdsOpen] = useState([]);
  const [simulationIsUpdated, setSimulationIsUpdated] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState({});
  const [allowScrollToId, setAllowScrollToId] = useState(true);
  const [dataModalFileView, setDataModalFileView] = useState({ isOpen: false, seriesData: {} });
  const [dataModalReferenceHistory, setDataModalReferenceHistory] = useState({
    isOpen: false,
    simReferenceHistoryData: {},
  });
  const [caseResult, setCaseResult] = useState(null);
  // const [multiResourcesSelected, setMultiResourcesSelected] = useState([]);
  const [singleResourceSelected, setSingleResourceSelected] = useState("");
  const [isAssignTagModalOpen, setIsAssignTagModalOpen] = useState(false);

  const { data: dataSimulator, refetch } = useQuery(
    ["getGNCSimulators6DOF"],
    () =>
      getSimulators6DOF({
        page: params?.page || 1,
        limit: PAGINATION_DEFAULT.pageSize,
        q: params?.condition,
        ...(params.tag_ids && { tag_ids: params.tag_ids }),
      }),
    {
      onSuccess: async (data) => {
        if (params?.id && allowScrollToId) {
          handleScrollTo(0, 90, `row${params.id}`);
          setAllowScrollToId(false);
        }
        for (const simulationItem of data.items) {
          if (
            simulationIdsOpen.some((id) => id === simulationItem.id) &&
            simulationItem.status === PROCESS_STATUS.FAILED &&
            !simulationIsUpdated.some((id) => id === simulationItem.id)
          ) {
            const simData = await updateSimulationData(simulationItem.id);
            handleIncludeMCSSuccessRate(simData);
            getSeriesDetail(simData);
            setSimulationIsUpdated((oldVal) => [...oldVal, simulationItem.id]);
          }
        }
      },
      onSettled: () => setIsLoadingSimulator(false),
      refetchInterval: REFETCH_INTERVAL_TIME,
    }
  );

  const getSimulators6DOFHistoryMutate = useMutation({
    mutationFn: (id) => getSimulators6DOFHistory(id),
  });
  const getSimulatorsDetail6DOFMutate = useMutation({
    mutationFn: (id) => getSimulatorsDetail6DOF(id),
  });

  const getPageIndexSimulator6DOFMutate = useMutation({
    mutationFn: (id) => getPageIndexSimulator6DOF(id),
  });

  const { data: allTagsData } = useGetAllTags({
    refetchOnWindowFocus: false,
    isSelectOptions: true,
  });

  const columnsSeriesTable = [
    {
      dataField: "param_filename",
      text: "Param Filename",
    },
  ];

  const handleIncludeMCSSuccessRate = async (simData) => {
    if (simData?.type !== SIMULATION_TYPE.GNC_6DoF_MCS) {
      delete simData.input_data.mcs_success_rate;
      return;
    }
    if (!simData?.series?.flight_statuses) {
      simData.input_data.mcs_success_rate = 0;
      return;
    }

    const flightStatuses = JSON.parse(simData.series.flight_statuses);
    const successRate = calculateMCSSuccessRate(flightStatuses);
    simData.input_data.mcs_success_rate = successRate;
  };

  const processMapData = (inputData = {}, mapingData) => {
    const columnsName = [];
    const columnsData = {};

    const addField = (field, text, data) => {
      let { value = data, unit = null } = typeof data === "object" ? data : {};
      columnsName.push({
        dataField: field,
        text: `${text}${unit ? ` (${unit})` : ""}`,
      });
      columnsData[field] = value;
    };

    mapingData.forEach(({ key, field, text }) => {
      let data = key
        ? key.split(".").reduce((obj, prop) => obj?.[prop], inputData)
        : inputData?.[field] === undefined
        ? "N/A"
        : inputData?.[field];
      addField(field, text, data);
    });

    return { columnsName, columnsData };
  };

  const handleDownload = async (id) => {
    let fileUrl = seriesById?.[id]?.simulationDetail?.result_url;
    if (!fileUrl) {
      const detailData = await getSimulatorsDetail6DOFMutate.mutateAsync(id);
      setSeriesById((oldVal) => ({
        ...oldVal,
        [id]: {
          ...oldVal?.[id],
          simulationDetail: detailData,
        },
      }));
      fileUrl = detailData.result_url;
    }
    window.open(fileUrl, "_blank");
  };

  const convertDataSeries = (data) => {
    let inputDataGNC6DOFMappingClone = [...INPUT_DATA_GNC_6DOF_MAPPING];
    if (data?.type === SIMULATION_TYPE.GNC_6DoF_MCS) {
      inputDataGNC6DOFMappingClone = [
        { key: "mcs_success_rate", field: "mcs_success_rate", text: "MCS Success Rate" },
        ...inputDataGNC6DOFMappingClone,
      ];
    } else {
      inputDataGNC6DOFMappingClone = inputDataGNC6DOFMappingClone.filter((item) => item.key !== "mcs_success_rate");
    }
    const columnsTable = processMapData(data?.input_data, inputDataGNC6DOFMappingClone);
    let terminalData = { columnsData: {}, columnsName: [] };
    if (data?.type === SIMULATION_TYPE.GNC_6DOF && (data.status === PROCESS_STATUS.SUCCEEDED || data.status === PROCESS_STATUS.FAILED)) {
      terminalData = processMapData(data?.series?.terminal, TERMINAL_GNC_6DOF_MAPPING);
    }
    delete data?.input_data?.mcs_success_rate;
    return {
      dataSeries: [
        {
          ...data,
          errorList: data?.errors?.length ? getErrorDetails(data?.errors, data?.series?.parameter_path, t) : [],
          paramList: {
            ...columnsTable.columnsData,
            ...terminalData.columnsData,
            param_filename: getFileUploadName(data?.series?.parameter_path),
          },
        },
      ],
      columnSeries: uniqArray([...columnsSeriesTable, ...uniqArray(columnsTable.columnsName), ...terminalData.columnsName]),
    };
  };

  const getSeriesDetail = (simulationData) => {
    const { id } = simulationData;
    const seriesData = convertDataSeries(simulationData);
    setSeriesById((oldVal) => ({
      ...oldVal,
      [id]: {
        data: seriesData.dataSeries,
        columns: seriesData.columnSeries,
        simulationDetail: simulationData,
        loading: false,
      },
    }));
  };

  const updateSimulationData = async (id) => {
    setSeriesById((oldVal) => ({
      ...oldVal,
      [id]: {
        ...oldVal?.[id],
        loading: true,
      },
    }));
    const simData = await getSimulatorsDetail6DOFMutate.mutateAsync(id);

    try {
      const fileResponse = await getContentFromLink(simData?.series?.parameter_url);

      if (fileResponse && typeof fileResponse.data === "object" && fileResponse.data !== null) {
        simData.paramsJsonContent = fileResponse.data;
      } else {
        simData.paramsJsonContent = {};
      }
      return simData;
    } catch (error) {
      simData.paramsJsonContent = {};
      return simData;
    }
  };

  const handleGetPageIndexById = async (simId, pageIndex) => {
    setPageIndexById((oldVal) => ({
      ...oldVal,
      [simId]: pageIndex,
    }));
  };

  const updatePageIndexById = async (simId) => {
    setPageIndexById((oldVal) => ({
      ...oldVal,
      [simId]: {
        ...oldVal?.[simId],
      },
    }));
    const pageIndex = await getPageIndexSimulator6DOFMutate.mutateAsync(simId);
    return pageIndex?.page;
  };

  const onClickCollapsed = async (row) => {
    const id = row?.id;
    if (simulationIdsOpen.some((id) => id === row.id)) {
      setSimulationIdsOpen((oldVal) => [...oldVal.filter((id) => id !== row.id)]);
      return;
    }
    setSimulationIdsOpen((oldVal) => [...oldVal, row.id]);
    let detailData = seriesById?.[id]?.simulationDetail;
    if (!detailData) {
      detailData = await updateSimulationData(id);
    }

    handleIncludeMCSSuccessRate(detailData);
    getSeriesDetail(detailData);
  };

  const onChangePage = (value) => {
    setParams({
      ...params,
      page: value,
    });
    handleScrollToTop();
  };

  const checkDashboardUrl = async (simulationData, params, retryCount) => {
    try {
      const res = await getDashboardLink(params);
      if (res.data.status === DASHBOARD_STATUS.INPROGRESS) {
        if (retryCount === RETRY_TIME) {
          showAlert({
            status: true,
            message: t("over_time"),
            type: "danger",
          });
          setDashboardLoading((oldVal) => ({ ...oldVal, [params.id]: false }));
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 3000));
        retryCount += 1;
        await checkDashboardUrl(simulationData, params, retryCount);
        return;
      }
      await refetch();
      setDashboardLoading((oldVal) => ({ ...oldVal, [params.id]: false }));
      if (res.data.dashboard_status === DASHBOARD_STATUS.SUCCEEDED) {
        window.open(res.data.dashboard_url, "_blank");
        return;
      }
    } catch (e) {
      setDashboardLoading((oldVal) => ({ ...oldVal, [params.id]: false }));
      showAlert({
        status: true,
        message: e?.response?.data?.error?.message || e?.response?.data?.message || t("common:network_error"),
        type: "danger",
      });
    }
  };

  const handleClickDashboard = async (row) => {
    const simulationData = row?.simulator_dashboard;
    if (simulationData?.dashboard_status === DASHBOARD_STATUS.SUCCEEDED && simulationData?.dashboard_url) {
      window.open(simulationData?.dashboard_url, "_blank");
      return;
    }
    setDashboardLoading({ ...dashboardLoading, [row.id]: true });
    const data = {
      id: row.id,
      dashboard_input_type: DASHBOARD_INPUT_TYPE.SINGLE,
      dashboard_type: row?.type === SIMULATION_TYPE.GNC_6DoF_MCS ? DASHBOARD_TYPE.GNC_6DOF_MCS : DASHBOARD_TYPE.GNC_6DOF,
      input_file_id: row.id,
    };

    await checkDashboardUrl(simulationData, data, 1);
  };

  const onKeyDownInput = (e) => {
    if (e.key === "Enter") {
      onSubmitSearch(e.target.value);
    }
  };

  const onSubmitSearch = (textSearch) => {
    if (params?.condition === textSearch && !isLoadingSimulator) {
      setIsLoadingSimulator(true);
      refetch();
    }
    setParams({
      page: 1,
      condition: textSearch,
    });
  };

  const getCurrentPage = () => {
    return (params?.page || PAGINATION_DEFAULT.page) - 1;
  };

  const handleOpenAssignTagsModal = (id = null) => {
    if (id) {
      setSingleResourceSelected(id.toString());
    }
    setIsAssignTagModalOpen(true);
  };

  const handleCloseAssignTagsModal = () => {
    setIsAssignTagModalOpen(false);
    setSingleResourceSelected("");
  };

  const handleFilterData = (name, vals) => {
    setParams((prevParams) => {
      const newParams = { ...prevParams, [name]: vals };
      newParams.page = 1;
      return newParams;
    });
  };

  useEffect(() => {
    setIsLoadingSimulator(true);
    refetch();
    setTextSearch(params?.condition);
  }, [params]);

  useEffect(() => {
    if (resetForm) {
      setTextSearch(null);
      setResetForm(false);
    }
  }, [resetForm]);

  return (
    <Container fluid className="list-gnc-simulator-6dof-container">
      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="header-title mb-0">{t("list.title")}</h2>
                <SearchInput
                  submit={onSubmitSearch}
                  setTextSearch={setTextSearch}
                  onKeyDownInput={onKeyDownInput}
                  textSearch={textSearch}
                  placeholder={t("list.search_placeholder")}
                />
              </div>
              <Row>
                <Col md={12}>
                  <Card>
                    <div className="d-flex justify-content-end">
                      <CardHeader className="bg-white border-0">
                        <ProtectedComponent id="gnc-simulator-new">
                          <CustomLink to={APP_ROUTES.GNC_SIMULATOR_NEW_6DOF} className="btn btn-primary text-capitalize">
                            {t("list.redirect_new")}
                          </CustomLink>
                        </ProtectedComponent>
                      </CardHeader>
                    </div>
                    <CardBody className="bg-white">
                      <TableCommon
                        loading={isLoadingSimulator || getSimulators6DOFHistoryMutate.isLoading}
                        columns={columns({
                          t,
                          setDataModalReferenceHistory,
                          getSimulators6DOFHistoryMutate,
                          setCaseResult,
                          handleClickDashboard,
                          handleDownload,
                          onClickCollapsed,
                          seriesById,
                          params,
                          dashboardLoading,
                          setDataModalFileView,
                          allTagsData,
                          handleFilterData,
                          // multiResourcesSelected,
                          handleOpenAssignTagsModal,
                        })}
                        data={dataSimulator?.items || []}
                        onChangePage={onChangePage}
                        tableFixed
                        tableClassName="table striped table-collapse table-fixed"
                        customClassName="overflow-auto simulation-table"
                        pageCurrent={getCurrentPage()}
                        pageCount={dataSimulator?.pagination?.total_page}
                        totalItem={dataSimulator?.pagination?.total}
                        pageSize={PAGINATION_DEFAULT.pageSize}
                        // enableRowSelection={!isEmpty(dataSimulator?.items)}
                        // onRowSelectionChange={(rowData) => {
                        //   const ids = rowData?.map((item) => item?.id?.toString());
                        //   setMultiResourcesSelected(ids);
                        // }}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Row>
      <ModalSeriesFilesView6DoF {...dataModalFileView} onClose={() => setDataModalFileView((oldVal) => ({ ...oldVal, isOpen: false }))} />
      <ModalReferenceHistory
        {...dataModalReferenceHistory}
        onClose={() => setDataModalReferenceHistory((oldVal) => ({ ...oldVal, isOpen: false }))}
        isGettingPageIndex={getPageIndexSimulator6DOFMutate.isLoading}
        pageIndexId={pageIndexId}
        handleGetPageIndexById={handleGetPageIndexById}
        updatePageIndexById={updatePageIndexById}
      />
      {caseResult && <ModalCaseResult closeModal={() => setCaseResult(null)} isOpen={!!caseResult} id={caseResult} />}
      <ModalAssignTags
        isOpen={isAssignTagModalOpen}
        onClose={handleCloseAssignTagsModal}
        resourceType={RESOURCE_TYPE_FOR_TAG.GNC_6DOF_SIMULATOR}
        resourcesSelected={[singleResourceSelected]}
        // resourcesSelected={isEmpty(singleResourceSelected) ? multiResourcesSelected : [singleResourceSelected]}
      />
    </Container>
  );
};

export default GNCSimulatorList6DOF;
